import { useEffect, useRef } from 'react';

import { useSelector } from 'react-redux';

import { trackCuredEvent, MixpanelEvent, MixpanelFlow, CampaignFlowType } from 'helpers/initMixpanel';
import { route as routeSelector } from 'state/selectors';

type UseCampaignFlowTrackingOptions = {
    flowType: CampaignFlowType;
    mainRoute: string;
};

/**
 * Custom hook to track campaign flow events (NEW_CAMPAIGN_START, AUDIENCE_SELECTED,
 * CONTENT_ADDED, SCHEDULE_SET) based on route changes.
 *
 * This centralizes the tracking logic for legacy campaign flows (Single Send, Email Series,
 * Conditional Split) to reduce code duplication.
 *
 * Notes:
 * - NEW_CAMPAIGN_START is tracked when the user lands on the main route (e.g., /curations/email/single-send)
 * - CONTENT_ADDED is tracked each time the user leaves a content page
 * - For flows with multiple content pages (Email Series, Conditional Split), this will fire multiple times
 *
 * @param options - Configuration object with flowType and mainRoute
 * @example
 * ```typescript
 * useCampaignFlowTracking({
 *   flowType: 'single_send',
 *   mainRoute: getCampaignRoute({ campaignKey })
 * });
 * ```
 */
export const useCampaignFlowTracking = ({ flowType, mainRoute }: UseCampaignFlowTrackingOptions) => {
    const route = useSelector(routeSelector);
    const prevRouteRef = useRef<string | null>(null);
    const hasTrackedStartRef = useRef(false);

    // Track NEW_CAMPAIGN_START when user lands on the main route
    useEffect(() => {
        if (route === mainRoute && !hasTrackedStartRef.current) {
            trackCuredEvent(
                MixpanelEvent.NEW_CAMPAIGN_START,
                {
                    campaign_type: 'email',
                    flow_type: flowType,
                },
                MixpanelFlow.CAMPAIGN
            );
            hasTrackedStartRef.current = true;
        }
    }, [route, mainRoute, flowType]);

    useEffect(() => {
        const prevRoute = prevRouteRef.current;

        // Track AUDIENCE_SELECTED when leaving audience page
        if (prevRoute?.includes('/audience') && !route.includes('/audience')) {
            trackCuredEvent(
                MixpanelEvent.AUDIENCE_SELECTED,
                {
                    campaign_type: 'email',
                    flow_type: flowType,
                },
                MixpanelFlow.CAMPAIGN
            );
        }

        // Track CONTENT_ADDED when leaving content page
        // Fires each time for flows with multiple content pages (Email Series, Conditional Split)
        if (prevRoute?.includes('/content') && !route.includes('/content')) {
            trackCuredEvent(
                MixpanelEvent.CONTENT_ADDED,
                {
                    campaign_type: 'email',
                    flow_type: flowType,
                },
                MixpanelFlow.CAMPAIGN
            );
        }

        // Track SCHEDULE_SET when leaving schedule page
        if (prevRoute?.includes('/schedule') && !route.includes('/schedule')) {
            trackCuredEvent(
                MixpanelEvent.SCHEDULE_SET,
                {
                    campaign_type: 'email',
                    flow_type: flowType,
                },
                MixpanelFlow.CAMPAIGN
            );
        }

        prevRouteRef.current = route;
    }, [route, mainRoute, flowType]);
};
